import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  numberAttribute,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';

import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { DateHelper } from '@app/date-helper';
import { BookingService } from '@app/hostel/booking.service';
import { SearchService } from '@app/search.service';
import {
  AllAvailabilityResponse,
  ApiService,
  AvailabilityResponse,
  AvailableDate,
  HOSTEL_HYDRATION_LEVEL,
  Rooms,
} from '@app/services/api/api.service';
@Component({
  selector: 'app-hostel-page',
  templateUrl: './hostel-page.component.html',
  styleUrls: ['./hostel-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate(
          '0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class HostelPageComponent implements OnChanges {
  private _mapContainer: ViewContainerRef;

  @ViewChild('mapContainer', { read: ViewContainerRef, static: false })
  set mapContainer(mapContainer: ViewContainerRef | undefined) {
    if (mapContainer && !this._mapContainer) {
      this._mapContainer = mapContainer;
      if (!this.mapLoaded && this.hostel) {
        this.loadMap(this.hostel);
      }
    }
  }

  @Input({ required: true })
  public id;
  @Input({ transform: numberAttribute, required: true })
  public noOfGuests = 1;
  @Input({ required: true })
  public checkInDate;
  @Input({ required: true })
  public checkOutDate;
  @Input({ required: true })
  public currency;

  public bottomSheetOpen = false;

  // DateHelper::normalizeDateToUTC(new Date())
  public unavailableDates: AvailableDate[] = [];
  public noAvailability = false;
  public showDialog = false;
  public hostel: any;
  public loading = false;
  public roomsLoading = false;
  public rooms: Rooms;
  public hydratedness: HOSTEL_HYDRATION_LEVEL = HOSTEL_HYDRATION_LEVEL.NONE;
  public HOSTEL_HYDRATION_LEVEL = HOSTEL_HYDRATION_LEVEL;
  public mapLoaded = false;
  public when: any;
  private destroyed = new Subject<void>();
  private reload = new Subject<void>();

  constructor(
    private readonly apiService: ApiService,
    public readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly searchService: SearchService,
    private readonly bookingService: BookingService,
    @Inject(PLATFORM_ID) private platformId: string,
    private router: Router,
    private _bottomSheet: MatBottomSheet
  ) {}

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  openBottomSheet(): void {
    this.bottomSheetOpen = true;
  }

  closeBottomSheet() {
    this.bottomSheetOpen = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['id']?.currentValue) {
      this.reload.next();
    }
    this.loading = true;
    const id = this.route.snapshot.paramMap.get('id') as string;

    if (isPlatformBrowser(this.platformId)) {
      this.route.data.pipe(take(1)).subscribe((data) => {
        if (data['hostel']) {
          this.hostel = data['hostel'].data;
          // if not fully hydrated, use whatever data is available, fetch full hostel
          if (data['hostel'].hydratedness < HOSTEL_HYDRATION_LEVEL.FULL) {
            this.hydratedness = data['hostel'].hydratedness;
            // fetch full hostel, still set loading to false
            this.loading = false;
            this.cdr.markForCheck();
            this.getFullHostel(this.hostel._id);
          } else {
            // page has already been visited
            this.hydratedness = data['hostel'].hydratedness;
            this.loading = false;
            this.loadMap(this.hostel);
            this.cdr.markForCheck();
          }
        } else {
          // initial page load, refresh, etc.
          this.getFullHostel(id);
        }
      });
    }

    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (
      changes['noOfGuests'] ||
      changes['checkInDate'] ||
      changes['checkOutDate'] ||
      changes['id'].currentValue
    ) {
      this.bookingService.clearSelection();

      // Set loading to true when checking availability
      this.roomsLoading = true;
      this.cdr.markForCheck();

      // this should only happen after getavailability is resolved, because
      // the route may have to change anyway
      this.apiService
        .checkAvailability(this.id, {
          checkInDate: DateHelper.normalizeDateToDateString(this.checkInDate),
          checkOutDate: DateHelper.normalizeDateToDateString(this.checkOutDate),
          noOfGuests: this.noOfGuests,
          currency: this.currency,
        })
        .pipe(take(1), takeUntil(this.destroyed), takeUntil(this.reload))
        .subscribe({
          next: (resp: AvailabilityResponse) => {
            if ((resp as any).errorCode === 1302) {
              this.rooms = {
                dormRooms: [],
                privateRooms: [],
                familyRooms: [],
              };
            } else {
              this.rooms = resp.data?.rooms ?? {
                dormRooms: [],
                privateRooms: [],
                familyRooms: [],
              };
            }
            // this.rooms = rooms; // hard coded to mock rooms
            this.roomsLoading = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            console.error('Error checking availability:', error);
            this.rooms = {
              dormRooms: [],
              privateRooms: [],
              familyRooms: [],
            };
            this.roomsLoading = false;
            this.cdr.markForCheck();
          },
        });
    }

    if (
      changes['noOfGuests'] ||
      (changes['id'] && changes['id'].currentValue)
    ) {
      const today = new Date();
      const sixMonthsFroMNow = new Date(today);
      sixMonthsFroMNow.setDate(sixMonthsFroMNow.getDate() + 182);

      // todo get first available day for # of guests (can include in seo route)
      this.apiService
        .getAvailability(this.id, {
          checkInDate: DateHelper.normalizeDateToDateString(today),
          checkOutDate: DateHelper.normalizeDateToDateString(sixMonthsFroMNow),
          noOfGuests: this.noOfGuests,
          currency: this.currency,
        })
        .pipe(take(1), takeUntil(this.destroyed), takeUntil(this.reload))
        .subscribe((resp: AllAvailabilityResponse) => {
          if (resp?.data?.date_wise_availability.length === 0) {
            this.noAvailability = true;
          } else {
            this.noAvailability = false;
          }
          this.unavailableDates = [
            ...new Set(
              (resp?.data?.date_wise_availability ?? [])
                .filter((d) => !d.available)
                .map((d) => {
                  return {
                    ...d,
                    date: DateHelper.normalizeDateToDateString(d.date),
                    // date: d.date.slice(0, 10)
                  };
                })
            ),
          ];

          let moved = false;
          let start = DateHelper.normalizeDateToDateString(this.checkInDate);
          const s = new Date(start);
          // only need to do this if not some future date?
          for (let i = 0; i < this.unavailableDates.length; i++) {
            if (start === this.unavailableDates[i].date) {
              moved = true;
              // const s = new Date(start);
              s.setDate(s.getDate() + 1);
              start = DateHelper.normalizeDateToDateString(s);
            } else {
              break;
            }
          }

          if (moved) {
            // this might mess up normalization
            const end = s;
            end.setDate(s.getDate() + 1);
            this.router.navigate([], {
              queryParams: {
                checkInDate: start,
                checkOutDate: DateHelper.formatDate(end),
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          } else {
            this.cdr.markForCheck();
          }

          // find first date that is available, and use that as the start date (immediately route to it)
          // in this component, dont even bother with search service. more complicated than it is worth
        });
    }
  }

  // todo when dates are changed, unset all selections
  ngAfterViewInit() {
    if (!this.mapLoaded && this.hostel) {
      this.loadMap(this.hostel);
    }
  }

  async loadMap(hostel: any) {
    if (this._mapContainer) {
      const { MapComponent } = await import('./map/map.component');
      this._mapContainer.clear();
      const componentRef = this._mapContainer.createComponent(MapComponent);
      componentRef.instance.hostel = hostel;
      componentRef.changeDetectorRef.detectChanges();
      this.mapLoaded = true;
    }
  }

  public open() {
    this.showDialog = true;
  }

  public close() {
    this.showDialog = false;
  }

  public scrollToAnchor(anchor: string) {
    const el = this.document.getElementById(anchor);
    if (el) {
      const elementRect = el.getBoundingClientRect();
      const elementTopPosition = window.scrollY + elementRect.top;

      window.scrollTo({
        top: elementTopPosition,
        behavior: 'smooth',
      });
    }
  }

  public getFullHostel(id: string) {
    this.apiService
      .getProperty(id)
      .pipe(take(1))
      .subscribe((resp: any) => {
        this.hydratedness = HOSTEL_HYDRATION_LEVEL.FULL;
        this.hostel = resp;
        this.cdr.markForCheck();
        this.loading = false;
        this.loadMap(this.hostel);
      });
  }

  ngOnDestroy() {
    this.reload.next();
    this.reload.complete();
    this.destroyed.next();
    this.destroyed.complete();
  }
}

// for routes that depend on query string, update navigate (in response to search service)
